import { t } from "@i18n";
import { SECTION_TYPE_TABLE_SECTION, formCategories, isTableSection, isWellFormedTableSection } from "@Helpers";
import { getPresetSectionsObject } from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/constants";
import type { QuestionField } from "@ServerTypes/forms/forms.schema";
import type { FormInstance, FormInstanceSection } from "../../formInstance.wire";

/**
 * One rule bag as `useFormValidator` (FormValidation/useFormValidator.tsx) consumes it. The four
 * bounds copied straight off the question come from `questionFieldSchema`; `required` /
 * `min|maxLength` / `min|maxRange` are the validator's own names for `isMandatory` /
 * `min|maxFieldLimit` / `min|maxFieldRange`. Kept in step with the `FieldRules` type declared next
 * to the validator itself — that one is local to that file, so it cannot be imported here.
 */
type FieldValidationRule = Pick<QuestionField, "minFieldRange" | "maxFieldRange" | "minNoOfFiles" | "maxNoOfFiles"> & {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  minRange?: number;
  maxRange?: number;
  /**
   * Only the two date-time scopes the validator branches on — `getDateTimeRules` below copies
   * `selectionScope` across only when it is one of these two. Must stay identical to the
   * `Extract<…>` in `useFormValidator`'s `FieldRules`: `updateRules` takes the rule bag as a
   * parameter, so the two types are compared contravariantly and any widening here breaks every
   * caller. Duplicated rather than imported because that file declares its rule types locally.
   */
  selectionScope?: Extract<QuestionField["selectionScope"], "futureDateTime" | "pastDateTime">;
  /** The current answer, echoed into the rule so `validateAll` can re-run without the field. */
  value?: unknown;
};

/**
 * One row of a table section, as `formTemplateFieldSchema.tableDataSource` stores them: a map of
 * question `_id` → captured answer, plus the row's own `rowId`. The schema types the array
 * `z.array(z.any())`, so this is the narrowest shape the readers here actually rely on.
 */
export type TableSectionRow = { rowId?: string | number } & Record<string, { value?: unknown } | undefined>;

/**
 * Read one `tableDataSource` entry as a row. The array is `z.array(z.any())` on the wire, so an entry
 * is only ever an interpretation of an untyped value: object-shaped entries pass through unchanged,
 * anything else — including the `null` that used to throw on the `rowId` read — reads as an empty
 * row, which registers the same no-answer rule an entry carrying no question already did. Mirrors
 * `readTableRow` in useForms.tsx, which does this for the same stored array.
 */
const isTableSectionRow = (row: unknown): row is TableSectionRow =>
  typeof row === "object" && row !== null && !Array.isArray(row);
const readTableSectionRow = (row: unknown): TableSectionRow => (isTableSectionRow(row) ? row : {});

/**
 * The per-level Fill/Modify/Verify user-id lists an instance carries on each of its template
 * sections. `inspectionAndProgress.collection.ts` models this as `z.record(z.string(), z.any())`, so
 * the inner shape is untyped at the source — reported as a server-schema gap rather than re-declared
 * here, because a guess made in web would not be enforced anywhere.
 */
export type SectionPermissions = FormInstanceSection["permissions"];

/**
 * Coerce a raw form-field value to a finite number, or `null` when the value is
 * blank/undefined/non-numeric. Used to build a numeric scope for formula
 * evaluation so a referenced-but-empty field leaves the formula incomplete
 * (evaluates to `null`) instead of being silently treated as `0`.
 */
export const toFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

/**
 * Join the resolved values of `selectedFields` (in order) using `seperator`.
 * Falsy values are skipped; returns "" when nothing resolves.
 */
export const buildConcatenatedValue = (
  selectedFields: Array<string> | undefined,
  valueById: Map<string, string>,
  seperator: string | undefined,
): string =>
  (selectedFields ?? [])
    .map((id) => valueById.get(id))
    .filter(Boolean)
    .join(seperator ?? "");

export const TEMPLATE_ENTITY_CREATION = "ENTITY_CREATION";
export const TEMPLATE_ENTITY_SIDEBAR = "ENTITY_SIDEBAR";

/**
 * Whether a mailbox/issues question is rendered by the surface named by `templateSource`: the create
 * form shows only `editableInForm` questions, the detail sidebar only `isMetadata` ones. Any other
 * resourceType renders every question.
 *
 * Both flags default to **true** when absent (forms/CLAUDE.md §7.5): a question authored before the
 * toggles existed — or imported from a Library section whose template type never carried them — has no
 * flag and must stay visible rather than silently disappearing. Only an explicit `false` hides one, and
 * the server agrees (`field.isMetadata !== false`, `field.editableInForm !== false`).
 *
 * `FormPreview`'s field filter and `addValidationToForms` MUST both read this one predicate. When they
 * disagree, a question the surface does not render still carries a `required` rule: `validateAll()`
 * fails on a field the user cannot see, and Create / Update silently does nothing.
 */
export const isQuestionVisibleInSurface = ({
  field,
  templateSource,
  resourceType,
}: {
  field: Pick<QuestionField, "editableInForm" | "isMetadata">;
  templateSource?: string;
  resourceType?: string;
}): boolean => {
  const isEntityModule = resourceType === formCategories.mailBox || resourceType === formCategories.issues;
  if (isEntityModule && templateSource) {
    if (templateSource === TEMPLATE_ENTITY_CREATION) return field.editableInForm ?? true;
    if (templateSource === TEMPLATE_ENTITY_SIDEBAR) return field.isMetadata ?? true;
    return false;
  }
  return true;
};

export const preSubmissionTypeDataSource = [
  { name: "Fill", value: "Fill" },
  { name: "Modify", value: "Modify" },
];
export const postSubmissionTypeDataSource = [
  { name: "Fill", value: "Fill" },
  { name: "Modify", value: "Modify" },
  { name: "Verify", value: "Verify" },
];
export const linkBoqSources = {
  INSPECTION_FORM: "inspectionForms",
  LINK_ITEMS: "linkItems",
};
export const formStatuses = {
  inProgress: "IN_PROGRESS",
  rejected: "REJECTED",
  approved: "APPROVED",
  submitted: "SUBMITTED",
};
export const formStatusFilterDataSource = [
  { name: "In Progress", value: formStatuses.inProgress },
  { name: "Rejected", value: formStatuses.rejected },
  { name: "Approved", value: formStatuses.approved },
  { name: "Submitted", value: formStatuses.submitted },
];

export const formSectionType = {
  FORM: "form",
  TABLE_SECTION: "tableSection",
};
/**
 * Is `userId` in one of a section's per-level user-id lists?
 *
 * The lists are `z.array(z.string())` on the wire (`sectionLevelPermissions`,
 * inspectionAndProgress.collection.ts), so `.includes` cannot be handed the possibly-absent current
 * user id directly. Nothing runs differently: that schema's census records all 40731 stored elements
 * as plain 24-hex user-id strings, so `list.includes(undefined)` is `false` on every document that
 * exists, which is what the short-circuit returns. Written as a call rather than a `userId &&` guard
 * at each site on purpose — the argument is evaluated BEFORE the id is tested, so a section whose
 * `permissions` map is missing the current level still throws exactly where it throws today (see the
 * KNOWN BUG on the level index below) instead of quietly becoming "no permission".
 */
const includesUser = (userIds: string[], userId: string | undefined): boolean =>
  userId !== undefined && userIds.includes(userId);

export const getUserPermissionForForm = (
  inspectionData: FormInstance,
  // Current user's id; may be absent before the session loads — `includesUser` then safely yields
  // no-permission, as `.includes(undefined)` did.
  userId: string | undefined,
  sectionIndex: number,
): boolean => {
  const isCurrentUserAllowed =
    // Four erased assertions per branch, none of which changes what runs:
    // `template[sectionIndex]` is `T | undefined` only because of `noUncheckedIndexedAccess` (the
    // caller resolved the index from the same array); `permissions` and `isArchived` are the
    // KNOWN BUGs documented on `getSectionPermission` / `getPermissions` below.
    //
    // KNOWN BUG, preserved verbatim (the fourth): `permissions[currentLevelOfForm]` is a record
    // lookup that can miss — a section carries a key only for the levels it takes part in — and it is
    // dereferenced unconditionally, so such a section throws a TypeError while the form is on that
    // level. This is live, not theoretical: 2 of the 1301 non-deleted cube-qa form instances have a
    // section with no entry for their current level. Asserted (erased at emit) to keep the existing
    // runtime; the fix is the `?.`-to-false path `getPermissions` below already uses, which is a
    // behaviour change and belongs in a bug fix, not a typing pass.
    (includesUser(
      inspectionData.selectedForm.template[sectionIndex]!.permissions![inspectionData.currentLevelOfForm]!.Fill,
      userId,
    ) ||
      includesUser(
        inspectionData.selectedForm.template[sectionIndex]!.permissions![inspectionData.currentLevelOfForm]!.Modify,
        userId,
      )) &&
    !inspectionData.isRejected &&
    !inspectionData.isApproved &&
    !inspectionData.isArchived!.status;

  return isCurrentUserAllowed;
};
export const getFillPermissionForForm = (
  inspectionData: FormInstance,
  userId: string | undefined,
  sectionIndex: number,
): boolean => {
  const isCurrentUserAllowed =
    !inspectionData.isRejected &&
    !inspectionData.isApproved &&
    !inspectionData.isArchived!.status &&
    includesUser(
      inspectionData.selectedForm.template[sectionIndex]!.permissions![inspectionData.currentLevelOfForm]!.Fill,
      userId,
    );

  return isCurrentUserAllowed;
};
export const getModifyPermissionForForm = (
  inspectionData: FormInstance,
  userId: string | undefined,
  sectionIndex: number,
  // Section ids the user has opened for modification. Declared as possibly holding `undefined`
  // because the ids it is tested against are `.optional()` on the wire; a real caller passes
  // `string[]`, which is assignable.
  modifySection: Array<string | undefined> | undefined,
): boolean | undefined => {
  const sectionId = inspectionData.selectedForm.template[sectionIndex]?._id;

  const isCurrentUserAllowed =
    !inspectionData.isRejected &&
    !inspectionData.isApproved &&
    !inspectionData.isArchived!.status &&
    includesUser(
      inspectionData.selectedForm.template[sectionIndex]!.permissions![inspectionData.currentLevelOfForm]!.Modify,
      userId,
    ) &&
    modifySection?.includes(sectionId);

  return isCurrentUserAllowed;
};

export const getSectionPermission = ({
  section,
  inspectionData,
  userId,
  type,
}: {
  section: FormInstanceSection;
  inspectionData: FormInstance;
  // Current user's id; may be absent before the session loads — `includesUser` then safely yields
  // no-permission, as `.includes(undefined)` did.
  userId: string | undefined;
  type: string;
}): boolean => {
  const isCurrentUserAllowed =
    includesUser(section.permissions![inspectionData.currentLevelOfForm]![type]!, userId) &&
    !inspectionData.isArchived!.status &&
    !inspectionData.isRejected &&
    !inspectionData.isApproved;

  return isCurrentUserAllowed;
};
export const getPermissions = ({
  inspectionData,
  permissions = {},
  type,
  userId,
}: {
  // `Partial` because the template-authoring and mailbox-sidebar paths call this with a form
  // *template* rather than an instance — the `?.` reads below are what make that work.
  inspectionData: Partial<FormInstance>;
  permissions: SectionPermissions;
  type: string;
  userId: string;
}): boolean => {
  if (typeof permissions == "object" && !Object.keys(permissions).length) {
    return true;
  }
  if (inspectionData.isApproved || inspectionData.isRejected || inspectionData.isArchived?.status) {
    return false;
  }
  // `currentLevelOfForm!` is an index-position assertion, erased at emit: on a template object the
  // property is absent, and `permissions[undefined]` resolves to `permissions["undefined"]` →
  // `undefined` → the `?.` chain short-circuits, exactly as before.
  return !!permissions[inspectionData.currentLevelOfForm!]?.[type]?.includes(userId);
};

export const getUrlFromField = (url: string) => {
  const newUrl = /^(https?:)?\/\//i.test(url) ? url : `https://${url}`;
  return newUrl;
};

const getTextRules = (field: QuestionField): FieldValidationRule => {
  return {
    minLength: field.minFieldLimit ?? 0,
    ...(field.maxFieldLimit && { maxLength: field.maxFieldLimit }),
  };
};

const getNumberRules = (field: QuestionField): FieldValidationRule => {
  return {
    minRange: field.minFieldRange ?? 0,
    ...(field.maxFieldRange && { maxRange: field.maxFieldRange }),
  };
};

const getFileRules = (field: QuestionField): FieldValidationRule => {
  return {
    ...(field.maxNoOfFiles && { maxNoOfFiles: field.maxNoOfFiles }),
    ...(field.minNoOfFiles && { minNoOfFiles: field.minNoOfFiles }),
  };
};

const getDateTimeRules = (field: QuestionField): FieldValidationRule => {
  const rules: FieldValidationRule = {
    ...(field.maxFieldRange && { maxFieldRange: field.maxFieldRange }),
    ...(field.minFieldRange && { minFieldRange: field.minFieldRange }),
  };
  // Compared against the two literals rather than through `.includes`: same test, but this one
  // narrows `selectionScope` to exactly the pair the rule bag admits, so neither side needs
  // asserting. `Array.prototype.includes` proved the same thing and could not narrow through it.
  if (field.selectionScope === "futureDateTime" || field.selectionScope === "pastDateTime") {
    rules.selectionScope = field.selectionScope;
  }
  return rules;
};

const generateRuleObj = (eachField: QuestionField, row?: TableSectionRow): FieldValidationRule => {
  const value = (row ? row[eachField._id]?.value : eachField.answer?.value) ?? "";

  let ruleObj: FieldValidationRule = {};

  if (eachField.isMandatory) {
    ruleObj.required = true;
  }

  switch (eachField.fieldType) {
    case "single-line":
    case "multi-line":
      ruleObj = { ...ruleObj, ...getTextRules(eachField) };
      break;
    case "number":
      ruleObj = { ...ruleObj, ...getNumberRules(eachField) };
      break;
    case "fileUpload":
    case "image":
      ruleObj = { ...ruleObj, ...getFileRules(eachField) };
      break;
    case "date-time":
      ruleObj = { ...ruleObj, ...getDateTimeRules(eachField) };
      break;
  }

  return { ...ruleObj, value };
};

/**
 * `FormInstanceSection` with only `tableDataSource` widened to `unknown[]`.
 *
 * This function is called with BOTH section shapes: form-INSTANCE sections (where the table row is
 * modelled as `tableSectionRow` in inspectionAndProgress.collection.ts) and form-TEMPLATE sections
 * from the file-upload grid, whose request schema still declares `tableDataSource: z.array(z.unknown())`.
 * That single property was the only incompatibility between them.
 *
 * Widening it here rather than tightening the request schema is deliberate: `formTemplateFieldSchema`
 * validates incoming bodies, and its own census note records 8 stored rows carrying an `_id` key that
 * the row schema's `.catchall(tableSectionCell)` would reject — tightening it could start 400-ing
 * real payloads. The row is annotated `TableSectionRow` at the single read below, and
 * `Array.prototype.forEach`'s callback parameter is bivariant, so that annotation still holds for
 * `unknown[]` and nothing about the runtime changes.
 *
 * The mapped type is homomorphic (`[K in keyof T]`), so optionality and the index signature carry over.
 */
type ValidationSection = {
  [K in keyof FormInstanceSection]: K extends "tableDataSource" ? unknown[] | undefined : FormInstanceSection[K];
};

export function addValidationToForms({
  formValidator,
  template = [],
  inspectionData,
  rows,
  modifySection = [],
  templateSource,
  resourceType,
}: {
  formValidator: { updateRules: (rules: Record<string, FieldValidationRule>) => void };
  template?: ValidationSection[];
  inspectionData?: FormInstance;
  /** Bulk-edit rows (file upload grid): the rule key is prefixed with each row's id. */
  rows?: Array<{ id?: string | number }>;
  modifySection?: Array<string | undefined>;
  templateSource?: string;
  resourceType?: string;
}) {
  // A question the surface does not render cannot be required in it — in EITHER direction. The sidebar
  // hides non-`isMetadata` questions and the create form hides non-`editableInForm` ones, so both drop
  // their `required` rule here. Reading the same predicate `FormPreview` filters with is what keeps the
  // two from disagreeing; a mismatch is invisible in the UI and ends in a dead-end failed validation.
  const dropRequiredIfHidden = (rules: FieldValidationRule, field: QuestionField) => {
    if (!isQuestionVisibleInSurface({ field, templateSource, resourceType })) {
      delete rules.required;
    }
  };

  const allSectionsInCurrentLevel: Array<string | undefined> = [];
  if (inspectionData?._id) {
    // `formFlow[currentLevelOfForm]` is `T | undefined` only under `noUncheckedIndexedAccess`, and
    // `dataSource` / `sections` are `.optional()` on the wire but read unconditionally here. Both
    // assertions are erased at emit; see the section-permission KNOWN BUG note above for the same
    // pattern on `permissions`.
    inspectionData.formFlow[inspectionData.currentLevelOfForm]!.dataSource!.map((data) => {
      if (data.type === "Fill") {
        data.sections!.map((section) => allSectionsInCurrentLevel.push(section._id));
      } else if (data.type === "Modify") {
        data.sections!.map((section) => {
          if (modifySection.includes(section._id)) {
            allSectionsInCurrentLevel.push(section._id);
          }
        });
      }
    });
    template = inspectionData.selectedForm.template;
  } else if (template.length) {
    template.forEach((section) => {
      allSectionsInCurrentLevel.push(section._id);
    });
  }
  const options: { rules: Record<string, FieldValidationRule> } = { rules: {} };
  template.forEach((section) => {
    if (isTableSection(section) && allSectionsInCurrentLevel.includes(section._id)) {
      // The two `!`s that used to stand here were not bookkeeping — a table section missing either
      // array threw `Cannot read properties of undefined (reading 'forEach')` and took the whole form
      // view down. `isWellFormedTableSection` is a real runtime guard, so the reads below are checked
      // rather than asserted, and a malformed section registers no rules instead of crashing. It does
      // not pass silently either: `checkMandatoryFields` refuses to submit the same section, matching
      // the server's `checkAllMandatoryFields` — the two sides now reject in lockstep.
      if (!isWellFormedTableSection(section)) return;
      section.questionFields.forEach((eachField) => {
        section.tableDataSource.forEach((rowValue) => {
          const row = readTableSectionRow(rowValue);
          const ruleName: string = `${row.rowId}${eachField._id}`;
          const rulesObj: FieldValidationRule = generateRuleObj(eachField, row);
          dropRequiredIfHidden(rulesObj, eachField);
          options.rules[ruleName] = {
            ...rulesObj,
          };
        });
      });
    } else if (section.questionFields && allSectionsInCurrentLevel.includes(section._id)) {
      section.questionFields.forEach((eachField) => {
        const ruleName = `${eachField._id}`;
        const rulesObj: FieldValidationRule = generateRuleObj(eachField);
        dropRequiredIfHidden(rulesObj, eachField);
        if (rows) {
          rows.forEach((row) => {
            options.rules[`${row.id}_${ruleName}`] = {
              ...rulesObj,
            };
          });
        } else {
          options.rules[ruleName] = {
            ...rulesObj,
          };
        }
      });
    }
  });
  formValidator.updateRules(options.rules);
}

export function checkMandatoryFields(inspectionData: FormInstance) {
  const allSectionsInCurrentLevel: Array<string | undefined> = [];
  // Same three erased assertions as in `addValidationToForms`: index access under
  // `noUncheckedIndexedAccess`, plus `dataSource` / `sections` being `.optional()` on the wire yet
  // dereferenced unconditionally here.
  inspectionData.formFlow[inspectionData.currentLevelOfForm]!.dataSource!.map((data) => {
    data.sections!.map((section) => allSectionsInCurrentLevel.push(section._id));
  });
  let errorToastContent = t("Form submission failed: Not all mandatory fields filled.");
  let isAllMandatoryFieldsFilled = true;

  const isEmptyValue = (value: unknown) => {
    if ((["", undefined, null] as unknown[]).includes(value)) return true;
    if (Array.isArray(value)) return value.join("").trim() === "";
    // The membership test above already returned for `null`; re-stating it here is what narrows
    // `unknown` down to a real object, so `Object.keys` needs nothing asserted.
    if (typeof value === "object" && value !== null && Object.keys(value).length === 0) return true;
    return false;
  };

  const validateMandatory = (isMandatory: boolean | undefined, value: unknown) => {
    if (isMandatory && isEmptyValue(value)) {
      isAllMandatoryFieldsFilled = false;
      return true;
    }
    return false;
  };

  const validateTextField = (field: QuestionField, value: unknown) => {
    // The answer arrives as `unknown` and only a string has characters to count, so the type is
    // tested rather than asserted. `useFormValidator`'s `checkStringBounds` gates on the same test,
    // which is what keeps the submit-time check and the live validator measuring the same values.
    if (!["single-line", "multi-line"].includes(field.fieldType) || typeof value !== "string" || !value.length) {
      return false;
    }

    if (field.minFieldLimit && field.minFieldLimit > value.length) {
      errorToastContent = t(`Form submission failed: Minimum characters criteria not met`);
      isAllMandatoryFieldsFilled = false;
      return true;
    }
    if (field.maxFieldLimit && value.length > field.maxFieldLimit) {
      errorToastContent = t(`Form submission failed: Entered value exceeds the max characters set for this field`);
      isAllMandatoryFieldsFilled = false;
      return true;
    }
    return false;
  };

  const validateDateTime = (field: QuestionField, value: unknown) => {
    if (field.fieldType !== "date-time") return false;
    // As in `validateTextField`: a date-time answer is epoch ms, and `useFormValidator`'s
    // `checkNumberBounds` gates on `typeof value === "number"` before applying the same bounds.
    if (typeof value !== "number") return false;

    if (field.selectionScope === "pastDateTime" && value && value > Date.now()) {
      errorToastContent = t("Form submission failed: Please select a date and time from the past");
      isAllMandatoryFieldsFilled = false;
      return true;
    }

    if (
      field.selectionScope === "rangeBased" &&
      ((field.minFieldRange && value && value < field.minFieldRange) ||
        (field.maxFieldRange && value && value > field.maxFieldRange))
    ) {
      errorToastContent = t("Form submission failed: Please select a date and time within the predefined range");
      isAllMandatoryFieldsFilled = false;
      return true;
    }

    return false;
  };

  const validateFiles = (field: QuestionField, value: unknown) => {
    if (!Array.isArray(value) || !value.length) return false;

    if (field.minNoOfFiles && field.minNoOfFiles > value.length) {
      errorToastContent = t(`Form submission failed: Minimun files criteria not met`);
      isAllMandatoryFieldsFilled = false;
      return true;
    }

    if (field.maxNoOfFiles && value.length > field.maxNoOfFiles) {
      isAllMandatoryFieldsFilled = false;
      errorToastContent = t("Form submission failed: Files uploaded exceeds the max limit set for this field");
      return true;
    }

    return false;
  };

  const generateRule = (eachField: QuestionField, row?: TableSectionRow) => {
    const answerValue: unknown = (row ? row[eachField._id]?.value : eachField.answer?.value) ?? "";

    // Every validator takes the raw `unknown` answer and narrows it itself, after re-checking
    // `fieldType` — nothing here has to claim a shape the value has not been shown to have.
    return (
      validateMandatory(eachField.isMandatory, answerValue) ||
      validateTextField(eachField, answerValue) ||
      validateDateTime(eachField, answerValue) ||
      validateFiles(eachField, answerValue)
    );
  };

  inspectionData.selectedForm.template.some((section) => {
    if (isTableSection(section) && allSectionsInCurrentLevel.includes(section._id)) {
      // Reject the malformed section rather than crash on it (the old `!`) or wave it through. The
      // wording and the trigger match the server's `checkAllMandatoryFields`, so the submit button
      // and the API agree: previously a section the client could not even read was one the server
      // either accepted outright (missing `questionFields`) or failed with a toast that named no
      // field (missing `tableDataSource`). Empty arrays are well-formed and keep their old path.
      if (!isWellFormedTableSection(section)) {
        errorToastContent = t(
          'Form submission failed: Table section "{{sectionHeading}}" is misconfigured and cannot be validated. Please contact your portal administrator.',
          { sectionHeading: section.sectionHeading },
        );
        isAllMandatoryFieldsFilled = false;
        return true;
      }
      section.questionFields.some((eachField) => {
        if (section.tableDataSource.length) {
          section.tableDataSource.some((row: TableSectionRow) => {
            return generateRule(eachField, row);
          });
        } else {
          return generateRule(eachField);
        }
      });
    } else if (section.questionFields && !section.sectionCode && allSectionsInCurrentLevel.includes(section._id)) {
      section.questionFields.some((eachField) => {
        return generateRule(eachField);
      });
      return !isAllMandatoryFieldsFilled;
    }
    return false;
  });
  return {
    errorToastContent,
    isAllMandatoryFieldsFilled,
  };
}
export const formStatusMapper: Record<string, string> = {
  [formStatuses.inProgress]: "In Progress",
  [formStatuses.rejected]: "Rejected",
  [formStatuses.approved]: "Approved",
  [formStatuses.submitted]: "Submitted",
};
export function getSectionName(section: { sectionCode?: string | null; sectionHeading?: string }) {
  // Widened to a string-keyed map: the literal returned by `getPresetSectionsObject` has literal
  // keys, which cannot be indexed by a plain `string`. Annotation only — no runtime change.
  const presetSectionsObject: Record<string, string> = getPresetSectionsObject();

  if (
    section.sectionCode &&
    section.sectionCode != SECTION_TYPE_TABLE_SECTION &&
    presetSectionsObject[section.sectionCode]
  ) {
    return presetSectionsObject[section.sectionCode];
  }
  return section.sectionHeading;
}
